/**
 * Plays one of coπ's fixed lines in a Gemini voice (lib/copi-voices). The
 * server renders each voice × language × line once (/api/copi/say); here the
 * file's address is kept for the visit, so a line is fetched once and then
 * plays at once. Callers fall back to the device's own voice when this throws
 * (signed out, offline, or a browser that won't play sound without a tap).
 */

import { fetchWithAuth } from '@/lib/fetch-with-auth';
import type { CopiVoiceLine } from '@/lib/copi-voices';

const urls = new Map<string, Promise<string>>();
let el: HTMLAudioElement | null = null;

export function copiVoiceUrl(voice: string, line: CopiVoiceLine, locale: string): Promise<string> {
  const key = `${voice}|${line}|${locale}`;
  let p = urls.get(key);
  if (!p) {
    p = fetchWithAuth(`/api/copi/say?${new URLSearchParams({ voice, line, locale })}`)
      .then(async res => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || typeof data?.url !== 'string') throw new Error(data?.error || `voice ${res.status}`);
        return data.url as string;
      });
    p.catch(() => urls.delete(key)); // a failure may be temporary — try again next time
    urls.set(key, p);
  }
  return p;
}

/** The element coπ speaks through — one, reused, so a tap that unlocked it keeps it unlocked. */
export function copiVoiceElement(): HTMLAudioElement {
  el ??= new Audio();
  return el;
}

export async function playCopiLine(voice: string, line: CopiVoiceLine, locale: string): Promise<void> {
  const url = await copiVoiceUrl(voice, line, locale);
  const a = copiVoiceElement();
  a.pause();
  a.src = url;
  await a.play();
}
